/**
 * Enrol a patient on an EHR connection: a patient search scoped to the
 * connection's account, with already-enrolled patients disabled, and an
 * optional external patient id. Every enrolment goes through matching.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import {
  MIN_ENROLL_QUERY_LENGTH,
  useEhrSyncEnrollCandidates,
  useEnrollEhrSyncPatient,
} from '@hooks/api/ehr-sync-patients';

import type { EhrEnrollCandidate } from '@models/ehr-sync';

import { formatClientId } from '@utils/format';

import { patientName, sexAbbreviation } from './patient-link.util';

const messages = defineMessages({
  title: { id: 'ehrSync.enroll.title', defaultMessage: 'Enroll patient' },
  patient: { id: 'ehrSync.enroll.patient', defaultMessage: 'Patient' },
  patientHelper: {
    id: 'ehrSync.enroll.patientHelper',
    defaultMessage: 'Searches patients in {organization} by name or VITALL ID.',
  },
  typeToSearch: {
    id: 'ehrSync.enroll.typeToSearch',
    defaultMessage: 'Type at least 2 characters',
  },
  noResults: {
    id: 'ehrSync.enroll.noResults',
    defaultMessage: 'No patients match',
  },
  alreadyEnrolled: {
    id: 'ehrSync.enroll.alreadyEnrolled',
    defaultMessage: 'Already enrolled',
  },
  vitallId: { id: 'ehrSync.enroll.vitallId', defaultMessage: 'VITALL ID {id}' },
  dob: { id: 'ehrSync.enroll.dob', defaultMessage: 'DOB {date}' },
  externalId: {
    id: 'ehrSync.enroll.externalId',
    defaultMessage: 'External patient ID (optional)',
  },
  externalIdHelper: {
    id: 'ehrSync.enroll.externalIdHelper',
    defaultMessage:
      "The EHR's ID for this patient. If entered, QIE pulls that record and runs the same matching on it. If blank, QIE searches the EHR on demographics.",
  },
  matchingNote: {
    id: 'ehrSync.enroll.matchingNote',
    defaultMessage:
      'The link starts as Pending. Every link goes through matching, with or without an ID. A score below the auto-link threshold goes to the review queue before any data syncs.',
  },
  cancel: { id: 'ehrSync.enroll.cancel', defaultMessage: 'Cancel' },
  enroll: { id: 'ehrSync.enroll.submit', defaultMessage: 'Enroll' },
});

// Waits for a pause in typing before searching, so each keystroke is not a request.
const SEARCH_DEBOUNCE_MS = 300;

/** Props for {@link EnrollPatientDialog}. */
interface EnrollPatientDialogProps {
  open: boolean;
  ehrConnectionId: string;
  /** The organization name, for the header and helper text. */
  organizationName: string;
  /** A second header line, e.g. vendor and EHR environment. */
  connectionLabel?: string | null;
  onClose: () => void;
  /** Called after a successful enrolment, with the patient's display name. */
  onEnrolled: (name: string) => void;
}

/**
 * Enrol dialog.
 *
 * @param props - see {@link EnrollPatientDialogProps}
 */
export default function EnrollPatientDialog({
  open,
  ehrConnectionId,
  organizationName,
  connectionLabel,
  onClose,
  onEnrolled,
}: EnrollPatientDialogProps) {
  const { formatMessage } = useIntl();
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<EhrEnrollCandidate | null>(null);
  const [externalId, setExternalId] = useState('');
  const { data: candidates, loading } = useEhrSyncEnrollCandidates(
    ehrConnectionId,
    query
  );
  const [enroll, enrolling] = useEnrollEhrSyncPatient();

  useEffect(() => {
    const timer = setTimeout(() => setQuery(input), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  // Reset when the dialog opens rather than when it closes, so the fields do
  // not clear while the close transition is still visible.
  useEffect(() => {
    if (open) {
      setInput('');
      setQuery('');
      setSelected(null);
      setExternalId('');
    }
  }, [open]);

  const close = () => {
    if (!enrolling) {
      onClose();
    }
  };

  const describe = (candidate: EhrEnrollCandidate) => {
    const { patient } = candidate;
    return [
      patient.birthDate
        ? formatMessage(messages.dob, { date: patient.birthDate })
        : null,
      sexAbbreviation(patient.gender),
      patient.vitallUserId !== null
        ? formatMessage(messages.vitallId, {
            id: formatClientId(patient.vitallUserId, false),
          })
        : null,
    ]
      .filter(Boolean)
      .join(' · ');
  };

  const submit = async () => {
    if (!selected) {
      return;
    }
    const saved = await enroll({
      ehrConnectionId,
      vitallPatientId: selected.patient.vitallPatientId,
      externalPatientId: externalId,
    });
    if (saved) {
      onEnrolled(patientName(selected.patient) ?? '');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>
        {formatMessage(messages.title)}
        <Typography
          component="span"
          display="block"
          variant="body2"
          color="text.secondary"
        >
          {[organizationName, connectionLabel].filter(Boolean).join(' · ')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Autocomplete<EhrEnrollCandidate>
            options={candidates}
            value={selected}
            onChange={(_event, value) => setSelected(value)}
            inputValue={input}
            onInputChange={(_event, value) => setInput(value)}
            // The backend already filtered; the local filter would hide
            // matches on the VITALL id.
            filterOptions={(options) => options}
            getOptionLabel={(option) => patientName(option.patient) ?? ''}
            getOptionDisabled={(option) => option.alreadyEnrolled}
            isOptionEqualToValue={(option, value) =>
              option.patient.vitallPatientId === value.patient.vitallPatientId
            }
            loading={loading}
            noOptionsText={formatMessage(
              input.trim().length < MIN_ENROLL_QUERY_LENGTH
                ? messages.typeToSearch
                : messages.noResults
            )}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                key={option.patient.vitallPatientId}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ width: '100%' }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {patientName(option.patient)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {describe(option)}
                    </Typography>
                  </Box>
                  {option.alreadyEnrolled ? (
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                      sx={{ textTransform: 'uppercase' }}
                    >
                      {formatMessage(messages.alreadyEnrolled)}
                    </Typography>
                  ) : null}
                </Stack>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={formatMessage(messages.patient)}
                helperText={formatMessage(messages.patientHelper, {
                  organization: organizationName,
                })}
              />
            )}
          />
          <TextField
            label={formatMessage(messages.externalId)}
            value={externalId}
            onChange={(event) => setExternalId(event.target.value)}
            helperText={formatMessage(messages.externalIdHelper)}
          />
          <Alert severity="info">{formatMessage(messages.matchingNote)}</Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={enrolling}>
          {formatMessage(messages.cancel)}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void submit();
          }}
          disabled={!selected || enrolling}
        >
          {formatMessage(messages.enroll)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
